import { useNavigate } from '@tanstack/react-router'
import { createContext, type ReactNode, useContext, useState } from 'react'
import { trackEvent } from '../../../lib/gtag'
import { ConnectLinkedInDialog } from '../components/connect-linkedin-dialog'
import type { RosterMatch } from '../components/outreach-active-view'
import { OutreachReviewMessagesDialog } from '../components/outreach-review-messages-dialog'
import type { LaunchStepId } from '../config/launch-checklist'
import { useLaunchOutreachMutation } from '../hooks/use-launch-outreach-mutation'
import {
  type LaunchChecklist,
  useOutreachLaunchChecklist,
} from '../hooks/use-outreach-launch-checklist'
import type { OutreachStatusData } from '../types'

interface LaunchFlowValue {
  startupId: string
  outreachStatus: OutreachStatusData | null
  checklist: LaunchChecklist
  /** Contact-count display string for CTAs/copy ("85", or "~85" while estimated). */
  contactsLabel: string
  /** Raw combined contact count — for the ROI calculator. */
  contactsCount: number
  /**
   * Open a launch step's action (navigate to profile / open a dialog / start
   * the LinkedIn connect). Jumping ahead of an incomplete step redirects to the
   * first incomplete one, so the flow can't be done out of order.
   */
  openStep: (id: LaunchStepId) => void
  /**
   * Reopen the review dialog after the review step is done (openStep treats
   * done steps as inert) — messages stay editable right up to launch. Pass
   * 'portfolio' to open straight onto the portfolio-founders tab.
   */
  editMessages: (initialTab?: 'investors' | 'portfolio') => void
  launch: () => void
  launchPending: boolean
  /**
   * Pre-launch channel on/off selection, held in memory only. The outreach row
   * (where the flags live) isn't created until provisioning, so the founder's
   * choice lives here and is persisted via selectChannels when they approve a
   * tab in the review dialog. Shared by the pre-launch tab bar and the dialog so
   * both stay in sync.
   */
  channelEnabled: (kind: 'investors' | 'portfolio') => boolean
  toggleChannel: (kind: 'investors' | 'portfolio') => void
}

const LaunchFlowContext = createContext<LaunchFlowValue | null>(null)

export function useLaunchFlow(): LaunchFlowValue {
  const context = useContext(LaunchFlowContext)
  if (!context) throw new Error('useLaunchFlow must be used within LaunchFlowProvider')
  return context
}

// Single owner of the pre-launch setup flow: the step actions (with ordering
// guards), the shared review/top-up dialogs, and the launch mutation. Lets the
// setup panel, the launch bar, and the board's unlock CTA all drive the same
// flow without duplicating dialog state.
export function LaunchFlowProvider({
  startupId,
  outreachStatus,
  contactsCount,
  contactsEstimated,
  investorContactsCount,
  investorContactsEstimated,
  previewContacts,
  children,
}: {
  startupId: string
  outreachStatus: OutreachStatusData | null
  /** Combined investor + portfolio count — for the hero/CTA copy. */
  contactsCount: number
  contactsEstimated: boolean
  /** Investor-only count — the review modal's Investors tab is per-channel. */
  investorContactsCount: number
  investorContactsEstimated: boolean
  /** Best-fit contacts (from matches), for the review dialog's preview switcher. */
  previewContacts: RosterMatch[]
  children: ReactNode
}) {
  const navigate = useNavigate()
  const checklist = useOutreachLaunchChecklist(startupId, outreachStatus)
  const launchMutation = useLaunchOutreachMutation(startupId)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewInitialTab, setReviewInitialTab] = useState<'investors' | 'portfolio'>('investors')
  const [connectOpen, setConnectOpen] = useState(false)

  // In-memory channel selection (see channelEnabled doc on LaunchFlowValue).
  // Seeded lazily once status loads: investors from the server flag, portfolio
  // always on pre-launch (founder opts out via the toggle).
  const [channels, setChannels] = useState<{ investors: boolean; portfolio: boolean } | null>(null)
  if (channels === null && outreachStatus) {
    setChannels({
      investors: outreachStatus.directInvestorOutreachEnabled ?? true,
      portfolio: true,
    })
  }
  const channelEnabled = (kind: 'investors' | 'portfolio') =>
    kind === 'investors' ? (channels?.investors ?? true) : (channels?.portfolio ?? true)
  const toggleChannel = (kind: 'investors' | 'portfolio') =>
    setChannels((prev) => {
      const base = prev ?? { investors: true, portfolio: true }
      return { ...base, [kind]: !base[kind] }
    })

  const openStep = (id: LaunchStepId) => {
    // Done steps are a record, not an action; jumping ahead to an incomplete
    // step lands on the first incomplete one instead.
    const target = checklist.steps.find((step) => step.id === id)
    if (target?.done) return
    const first = checklist.firstIncomplete
    const effective = first && first.id !== id ? first.id : id
    trackEvent('outreach_setup_step_clicked', { startup_id: startupId, step: effective })

    switch (effective) {
      case 'profile':
        navigate({
          to: '/app/startup/$id/profile',
          params: { id: startupId },
          search: checklist.profileFocus ? { focus: checklist.profileFocus } : {},
        })
        break
      case 'review':
        setReviewOpen(true)
        break
      case 'linkedin':
        // Explainer dialog first — nothing is provisioned until the founder
        // clicks its "Email me the sign-in link" CTA. The dialog then tracks
        // the async HeyReach flow (invitation → sign-in → connected).
        setConnectOpen(true)
        break
    }
  }

  const launch = () => {
    if (!checklist.canCallLaunchMutation || launchMutation.isPending) return
    launchMutation.mutate(undefined, {
      onSuccess: () => trackEvent('outreach_campaign_launched', { startup_id: startupId }),
    })
  }

  const value: LaunchFlowValue = {
    startupId,
    outreachStatus,
    checklist,
    contactsLabel: `${contactsEstimated ? '~' : ''}${contactsCount}`,
    contactsCount,
    openStep,
    editMessages: (initialTab) => {
      setReviewInitialTab(initialTab ?? 'investors')
      setReviewOpen(true)
    },
    launch,
    launchPending: launchMutation.isPending,
    channelEnabled,
    toggleChannel,
  }

  return (
    <LaunchFlowContext.Provider value={value}>
      {children}
      <OutreachReviewMessagesDialog
        // Remount when the requested tab changes so the dialog opens on it
        // (the tab is seeded from initialTab on mount).
        key={reviewInitialTab}
        startupId={startupId}
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        contacts={previewContacts}
        totalContacts={investorContactsCount}
        contactsLabel={`${investorContactsEstimated ? '~' : ''}${investorContactsCount}`}
        initialTab={reviewInitialTab}
      />
      <ConnectLinkedInDialog
        startupId={startupId}
        open={connectOpen}
        onOpenChange={setConnectOpen}
        outreachStatus={outreachStatus}
        contactsLabel={value.contactsLabel}
        onConnectedContinue={() => setConnectOpen(false)}
      />
    </LaunchFlowContext.Provider>
  )
}
